import React from 'react';
import { PencilIcon, TrashIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Never';
  
  // Handle Firebase timestamp
  if (timestamp.seconds && timestamp.nanoseconds) {
    return new Date(timestamp.seconds * 1000).toLocaleDateString();
  }
  
  // Handle ISO string
  if (typeof timestamp === 'string') {
    try {
      return new Date(timestamp).toLocaleDateString();
    } catch (e) {
      return timestamp;
    }
  }
  
  return String(timestamp);
};

const getSortIcon = (field, sortField, sortDirection) => {
  if (field !== sortField) {
    return (
      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }
  
  return sortDirection === 'asc' ? (
    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
    </svg>
  ) : (
    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
    </svg>
  );
};

export default function CompanyListTable({
  companies,
  selectedCompanies,
  onCompanySelect,
  onCompanyEdit,
  onCompanyDelete,
  onSort,
  sortField,
  sortDirection,
  onBulkSelect,
  loading = false
}) {
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      onBulkSelect(companies.map(company => company.id));
    } else {
      onBulkSelect([]);
    }
  };

  const handleSelectCompany = (companyId) => {
    if (selectedCompanies.includes(companyId)) {
      onBulkSelect(selectedCompanies.filter(id => id !== companyId));
    } else {
      onBulkSelect([...selectedCompanies, companyId]);
    }
  };

  const isAllSelected = companies.length > 0 && selectedCompanies.length === companies.length;
  const isIndeterminate = selectedCompanies.length > 0 && selectedCompanies.length < companies.length;

  if (loading) {
    return (
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-4">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (companies.length === 0) {
    return (
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-12 text-center">
          <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No companies found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new company.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200" role="table" aria-label="Companies table">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="relative w-12 px-6 sm:w-16 sm:px-8">
                <input
                  type="checkbox"
                  className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 focus:ring-2 focus:ring-offset-2 sm:left-6"
                  ref={(el) => {
                    if (el) el.indeterminate = isIndeterminate;
                  }}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  aria-label={isAllSelected ? 'Deselect all companies' : 'Select all companies'}
                />
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                onClick={() => onSort('name')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSort('name');
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Sort by company name ${sortField === 'name' ? (sortDirection === 'asc' ? 'descending' : 'ascending') : 'ascending'}`}
              >
                <div className="flex items-center space-x-1">
                  <span>Company</span>
                  {getSortIcon('name', sortField, sortDirection)}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                onClick={() => onSort('industry')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSort('industry');
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Sort by industry ${sortField === 'industry' ? (sortDirection === 'asc' ? 'descending' : 'ascending') : 'ascending'}`}
              >
                <div className="flex items-center space-x-1">
                  <span>Industry</span>
                  {getSortIcon('industry', sortField, sortDirection)}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                onClick={() => onSort('location')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSort('location');
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Sort by location ${sortField === 'location' ? (sortDirection === 'asc' ? 'descending' : 'ascending') : 'ascending'}`}
              >
                <div className="flex items-center space-x-1">
                  <span>Location</span>
                  {getSortIcon('location', sortField, sortDirection)}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                onClick={() => onSort('userCount')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSort('userCount');
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Sort by user count ${sortField === 'userCount' ? (sortDirection === 'asc' ? 'descending' : 'ascending') : 'ascending'}`}
              >
                <div className="flex items-center space-x-1">
                  <span>Users</span>
                  {getSortIcon('userCount', sortField, sortDirection)}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                onClick={() => onSort('jobCount')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSort('jobCount');
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Sort by job count ${sortField === 'jobCount' ? (sortDirection === 'asc' ? 'descending' : 'ascending') : 'ascending'}`}
              >
                <div className="flex items-center space-x-1">
                  <span>Jobs</span>
                  {getSortIcon('jobCount', sortField, sortDirection)}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                onClick={() => onSort('createdAt')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSort('createdAt');
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Sort by creation date ${sortField === 'createdAt' ? (sortDirection === 'asc' ? 'descending' : 'ascending') : 'ascending'}`}
              >
                <div className="flex items-center space-x-1">
                  <span>Created</span>
                  {getSortIcon('createdAt', sortField, sortDirection)}
                </div>
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {companies.map((company) => (
              <tr 
                key={company.id} 
                className={`hover:bg-gray-50 cursor-pointer ${selectedCompanies.includes(company.id) ? 'bg-purple-50' : ''}`}
                onClick={(e) => {
                  // Don't trigger row click if clicking on checkbox or action buttons
                  if (e.target.type === 'checkbox' || e.target.closest('button')) {
                    return;
                  }
                  onCompanyEdit(company);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onCompanyEdit(company);
                  }
                }}
                aria-label={`Edit ${company.name}`}
              >
                <td className="relative w-12 px-6 sm:w-16 sm:px-8" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 focus:ring-2 focus:ring-offset-2 sm:left-6"
                    checked={selectedCompanies.includes(company.id)}
                    onChange={() => handleSelectCompany(company.id)}
                    aria-label={`Select ${company.name}`}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      {company.logo ? (
                        <img 
                          className="h-10 w-10 rounded-full object-cover" 
                          src={company.logo} 
                          alt={`${company.name} logo`}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center ${company.logo ? 'hidden' : 'flex'}`}
                      >
                        <span className="text-sm font-medium text-purple-600">
                          {company.name?.charAt(0)?.toUpperCase() || 'C'}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {company.name || 'Unnamed Company'}
                      </div>
                      {company.website && (
                        <div className="text-sm text-gray-500">
                          <a 
                            href={company.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="hover:text-purple-600"
                          >
                            {company.website}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                    {company.industry || 'Not specified'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {company.location || 'Not specified'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center">
                    <span className="text-lg font-semibold text-gray-900">
                      {company.userCount || 0}
                    </span>
                    {company.userCount > 0 && (
                      <span className="ml-1 text-xs text-gray-500">users</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center">
                    <span className="text-lg font-semibold text-gray-900">
                      {company.jobCount || 0}
                    </span>
                    {company.jobCount > 0 && (
                      <span className="ml-1 text-xs text-gray-500">jobs</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatTimestamp(company.createdAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => onCompanyEdit(company)}
                      className="text-purple-600 hover:text-purple-900 p-1 rounded-md hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                      title={`Edit ${company.name}`}
                      aria-label={`Edit ${company.name}`}
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onCompanyDelete(company.id)}
                      className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                      title={`Delete ${company.name}`}
                      aria-label={`Delete ${company.name}`}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}